import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { OrganizationsService } from 'shared/services/organizations/organizationsService';
import { IListagemOrganization } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useHasPermissionAcao } from 'shared/hooks';

export const ListagemDeOrganizacao: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const hasPermitionCreate = useHasPermissionAcao('organizacao', 'CRE');
  const hasPermitionRead = useHasPermissionAcao('organizacao', 'REA');


  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const [rows, setRows] = useState<IListagemOrganization[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaId = useMemo(() => {
    return searchParams.get('codigoOrganizacao') || [];
  }, [searchParams]);

  const buscaNome = useMemo(() => {
    return searchParams.get('organizacaoNome') || '';
  }, [searchParams]);

  const buscaSigla = useMemo(() => {
    return searchParams.get('organizacaoSigla') || '';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);

  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);



  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: t('pages.organizationSeletion.codigoOrganizacao'),
      textoBusca: buscaId,
      handleTextoBusca: texto => { searchParams.set('codigoOrganizacao', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.organizationSeletion.organizacaoNome'),
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('organizacaoNome', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextFieldSigla',
      mapValue: [],
      label: t('pages.organizationSeletion.organizacaoSigla'),
      textoBusca: buscaSigla,
      handleTextoBusca: texto => { searchParams.set('organizacaoSigla', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
  ];

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({ ...stateSnack, open: false });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString());
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  useEffect(() => {
    //console.log();
    setIsLoading(true);
    OrganizationsService.getAll(searchParams.toString())
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          //console.log(result);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString()) / Number.parseInt(limit.toString()));
        }
      });
  }, [eventoPesquisar]);

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.organizationSeletion.organization')}
        botaoLog={
          <Button
            color='info'
            disableElevation
            variant='contained'
            size='small'
            onClick={() => navigate('/organizacao/listagem/logs')}
            startIcon={<Icon>crop</Icon>}
          >
            <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
              {t('components.ferramentasDaListagem.logs')}
            </Typography>
          </Button>
        }
        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >     

        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell>{t('global.acoes')}</TableCell>
                <TableCell>{t('pages.organizationSeletion.codigoOrganizacao')}</TableCell>
                <TableCell>{t('pages.organizationSeletion.organizacaoNome')}</TableCell>
                <TableCell>{t('pages.organizationSeletion.organizacaoSigla')}</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.CodigoOrganizacao}>
                  <TableCell>
                    <IconButton size="small" disabled={!hasPermitionRead} onClick={() => navigate(`/organizacao/detalhe/${row.CodigoOrganizacao}`)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.CodigoOrganizacao}</TableCell>
                  <TableCell>{row.OrganizacaoNome}</TableCell>
                  <TableCell>{row.OrganizacaoSigla}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/organizacao/detalhe/novo')}
                      startIcon={<Icon>add</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.novo')}
                      </Typography>
                    </Button>

                    )}


                  </TableCell>
                  <TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5, 8, 10, 20, 50, 100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </LayoutBaseDePagina>
      <AlertaSnackBar
        open={stateSnack.open}
        severity={stateSnack.severity as AlertColor}
        message={stateSnack.message}
        handleClose={handleCloseSnack}
      />
    </>
  );
};